import { useMemo } from "react";
import { useFlagOnaAIEnabled, useFlagOnaEnvironmentOnboardingEnabled } from "@/hooks/use-flag";
import { Agents, useAgentExecutionsByEnvironment, type PlainAgentExecution } from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";

export type ConversationPanelType =
    | {
          type: "onboarding";
      }
    | { type: "remote-agent-execution"; agentExecution: PlainAgentExecution }
    | { type: "in-environment-agent-execution"; agentExecution?: PlainAgentExecution };

export const useEnvironmentConversation = (environmentId?: string) => {
    const { data: environment, isPending: isEnvironmentPending } = useEnvironment(environmentId);

    const { data: onboardingAgentExecution, isPending: isOnboardingAgentExecutionPending } =
        useAgentExecutionsByEnvironment({
            environmentId,
            agentId: Agents.DevelopmentEnvironmentSetup.id,
        });

    const { data: remoteAgentExecution, isPending: isRemoteAgentExecutionPending } = useAgentExecutionsByEnvironment({
        environmentId,
        agentId: Agents.Ona.id,
    });

    const { data: inEnvironmentAgentExecution, isPending: isInEnvironmentAgentExecutionPending } =
        useAgentExecutionsByEnvironment({
            environmentId,
            agentId: Agents.InEnvironmentOna.id,
        });

    const { value: isOnaEnvironmentOnboardingEnabled } = useFlagOnaEnvironmentOnboardingEnabled();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();
    const isFeatureFlagEnabled = isOnaEnabled || isOnaEnvironmentOnboardingEnabled;

    const isPending = environmentId
        ? isEnvironmentPending ||
          isOnboardingAgentExecutionPending ||
          isRemoteAgentExecutionPending ||
          isInEnvironmentAgentExecutionPending
        : false;

    const createdFromProject = environment?.metadata?.projectId;
    const showOnboardingConversation =
        isOnaEnvironmentOnboardingEnabled &&
        !isPending &&
        (!createdFromProject || onboardingAgentExecution !== undefined);

    const environmentConversation: ConversationPanelType | null = useMemo(() => {
        if (!isFeatureFlagEnabled || !environmentId || isPending) {
            return null;
        }

        if (remoteAgentExecution !== undefined) {
            return {
                type: "remote-agent-execution",
                agentExecution: remoteAgentExecution,
            };
        }

        if (inEnvironmentAgentExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentAgentExecution,
            };
        }

        if (showOnboardingConversation) {
            return {
                type: "onboarding",
            };
        }

        return null;
    }, [
        isFeatureFlagEnabled,
        environmentId,
        isPending,
        remoteAgentExecution,
        inEnvironmentAgentExecution,
        showOnboardingConversation,
    ]);

    return {
        environmentConversation,
        isPending,
    };
};
